<template>
    <d-card class="h-100">
        <div>
            <loading
                :active="isLoading"
                :can-cancel="false"
                :is-full-page="true"
            >
            </loading>
        </div>
        <google-analytics-form
            :receivedModalType="modalType"
            :hasGoogleConnection="hasGoogleConnection"
            :config="config"
            :services="services"
        />
        <d-card-header
            :class="{ 'border-bottom success': config }"
            class="border-bottom"
            :style="{ 'border-radius': '0.625rem 0.625rem 0 0' }"
            >Google Analytics</d-card-header
        >
        <d-card-body>
            <d-col class="d-flex justify-content-center">
                <img
                    class="img-fluid"
                    src="@/assets/images/google-analytics.jpg"
                    alt="ga"
                />
            </d-col>
        </d-card-body>
        <div
            class="text-center w-75 mx-auto"
            v-if="!hasGoogleConnection && !config"
        >
            <button @click="getGAAuthUrl" class="google-sign-in">
                <div class="google-logo">
                    <img style="vertical-align: unset;" src="../../../assets/images/google-logo-icon-sign-in.svg" width="16px" height="16px">
                </div>
                <div class="google-text">
                    Sign in with Google
                </div>
            </button>
        </div>
        <d-card-footer v-else class="d-flex justify-content-center">
            <button
                v-if="config"
                @click="updateGAIntegration"
                class="btn btn-secondary"
            >
                Connected
            </button>
            <button
                v-else
                @click="showForm(modalTypes.CONNECT_VIA_OAUTH)"
                class="btn btn-success"
            >
                Connect
            </button>
        </d-card-footer>
    </d-card>
</template>

<script>
import {
    GaService,
    GoogleAnalyticsOAuthService,
    WebsiteService,
    ErrorService
} from "@/main";
import notification from "@/mixins/notification";
import modalTypes from "@/components/integrations/google-analytics-oauth/modalTypes";
import GoogleAnalyticsForm from "@/components/integrations/google-analytics-oauth/GoogleAnalyticsForm.vue";
import * as Sentry from "@sentry/browser";
import Loading from "vue-loading-overlay";

export default {
    mixins: [notification],
    components: {
        "google-analytics-form": GoogleAnalyticsForm, Loading
    },

    props: {
        config: null,
        hasGoogleConnection: null
    },

    data() {
        return {
            services: [],
            modalType: {},
            modalTypes: modalTypes,
            isLoading: false
        };
    },

    methods: {
        updateGAIntegration() {
            if (this.hasGoogleConnection) {
                this.showForm(modalTypes.UPDATE_VIA_OAUTH_CONNECTION);
            } else {
                this.showForm(modalTypes.UPDATE_GOOGLE_ID_CONNECTION);
            }
        },

        closeForm() {
            this.$eventHub.$emit("close-google-analytics-form");
        },

        showForm(type) {
            this.$eventHub.$emit("show-google-analytics-form", type);
        },

        async getGAAuthUrl() {
            try {
                $cookies.set("website_id", this.$route.params.id);
                const res = await GoogleAnalyticsOAuthService.getGAAuthUrl();
                const urlToGAOAuth = res.data.gaAuthUrl;
                window.location.href = urlToGAOAuth;
            } catch (e) {
                this.showNotification(
                    "error-notification",
                    "Oops! We have a problem",
                    ErrorService.getErrorMessage(
                        e.networkError.result.errors[0]
                    )
                );
                Sentry.captureException(e);
                console.log(e);
            }
        },

        async googleGetProfiles() {
            try {
                this.isLoading = true
                const res = await GoogleAnalyticsOAuthService.googleGetProfiles();
                const services = JSON.parse(res.data.googleGetProfiles);
                this.services = services;
                if (this.hasGoogleConnection && !this.config) {
                    this.showForm(modalTypes.CONNECT_VIA_OAUTH);
                }
                this.isLoading = false
            } catch (e) {
                this.isLoading = false
                let error = { ...e };
                Sentry.captureException(e);
                if (this.haveUserGoogleAnalyticsAccount(error)) {
                    this.showNotification(
                        "error-notification",
                        "You do not have any Google Analytics account."
                    );
                    this.disconnectIntegration();
                }
                if (
                    this.haveUserGoogleAnalyticsAccount(error) &&
                    error.graphQLErrors.length === 1
                ) {
                    return;
                }
                this.showNotification(
                    "error-notification",
                    "Oops! We have a problem"
                );
            }
        },

        haveUserGoogleAnalyticsAccount(error) {
            const noAccountError = error.graphQLErrors.find(el => {
                const errorObject = JSON.parse(el.debugMessage);
                return !!(
                    errorObject.error.hasOwnProperty("code") &&
                    errorObject.error.code === 403
                );
            });
            return noAccountError;
        },

        async disconnectIntegration() {
            try {
                await GoogleAnalyticsOAuthService.removeGoogle(
                    this.$route.params.id
                );
                this.$router.go();
            } catch (e) {
                this.showNotification(
                    "warn",
                    "Oops! we have a problem",
                    "Can not remove integration"
                );
                Sentry.captureException(e);
                console.log(e);
            }
        },

        initUpdateConnectionForm() {
            this.googleId = this.config.google_id;
            this.isAlwaysPushPageViews = this.config.is_always_push_page_views;
            this.selectedCronTime = this.config.send_disapprove_transactions;
        }
    },

    mounted() {
        if (this.hasGoogleConnection) {
            this.googleGetProfiles();
        }
    },

    beforeUpdate() {
        if (!!this.config) {
            this.initUpdateConnectionForm();
        }
    }
};
</script>

<style>
    .google-sign-in {
        display: flex;
        width: 100%;
        height: 40px;
        margin: 0 auto 16px auto;
        padding: 0;
        border-radius: 2px;
        border: none;
        background-color: #4285f4;
        outline: none;
    }

    .google-sign-in:hover {
        background-color: #2b77f3;
    }

    .google-sign-in:focus {
        background-color: #097bed;
        outline: #097bed solid 1px;
        outline-offset: 2px;
    }

    .google-logo {
        height: 40px;
        width: 40px;
        padding: 12px;
        background-color: #ffffff;
        border: 1px solid #4285f4;
        border-radius: 1px;
    }

    .google-text {
        width: 100%;
        height: 40px;
        color: #fff;
        line-height: 2.8;
        font-size: 14px;
        letter-spacing: 0.21875px;
    }
</style>
